import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function AboutPage() {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // 실제로는 API 호출이 필요하지만, 현재는 시뮬레이션
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    setSubmitted(true);
    setIsSubmitting(false);
    setContactForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700 py-4 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <span>←</span>
            <span>홈으로</span>
          </button>
          <h1 className="text-lg font-semibold">서비스 소개</h1>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* About Section */}
        <section className="mb-12">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🎙️</div>
            <h2 className="text-3xl font-bold mb-2">말싸움</h2>
            <p className="text-slate-400">실시간 음성 토론 배틀 게임</p>
          </div>

          <div className="bg-slate-800/50 rounded-2xl p-6 mb-6">
            <h3 className="text-xl font-semibold mb-4">🎯 서비스 소개</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              <strong>말싸움</strong>은 다양한 사회적 주제에 대해 실시간 음성으로 토론을 펼치는 
              새로운 형태의 토론 플랫폼입니다. 찬성과 반대 두 진영으로 나뉘어 배심원들 앞에서 
              자신의 논리를 펼치고, 투표로 승패를 가립니다.
            </p>
            <p className="text-slate-300 leading-relaxed">
              텍스트 기반의 기존 토론 플랫폼과 달리, 실제 대화처럼 생생한 음성 토론을 통해 
              더 역동적이고 몰입감 있는 토론 경험을 제공합니다. 게임화된 요소(티어, 경험치, 랭킹)를 
              통해 건전한 토론 문화를 장려합니다.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-2xl p-6 mb-6">
            <h3 className="text-xl font-semibold mb-4">💡 비전</h3>
            <ul className="space-y-3 text-slate-300">
              <li className="flex gap-3">
                <span>•</span>
                <span>다양한 의견을 존중하는 건전한 토론 문화 확산</span>
              </li>
              <li className="flex gap-3">
                <span>•</span>
                <span>논리적 사고와 표현력 향상을 위한 플랫폼 제공</span>
              </li>
              <li className="flex gap-3">
                <span>•</span>
                <span>세대와 지역을 넘어 소통하는 열린 공간 구축</span>
              </li>
            </ul>
          </div>

          <div className="bg-slate-800/50 rounded-2xl p-6">
            <h3 className="text-xl font-semibold mb-4">📊 서비스 정보</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">서비스명</p>
                <p className="text-slate-300">말싸움 (MalSSaum)</p>
              </div>
              <div>
                <p className="text-slate-500">출시일</p>
                <p className="text-slate-300">2024년</p>
              </div>
              <div>
                <p className="text-slate-500">플랫폼</p>
                <p className="text-slate-300">웹 (모바일/PC)</p>
              </div>
              <div>
                <p className="text-slate-500">지원 언어</p>
                <p className="text-slate-300">한국어</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">📬 문의하기</h2>
          
          {submitted ? (
            <div className="bg-green-900/30 border border-green-700 rounded-2xl p-8 text-center">
              <div className="text-4xl mb-4">✅</div>
              <h3 className="text-xl font-semibold mb-2">문의가 접수되었습니다</h3>
              <p className="text-slate-400 mb-4">
                빠른 시일 내에 답변 드리겠습니다.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="text-primary-400 hover:text-primary-300 transition-colors"
              >
                새 문의 작성하기
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-slate-800/50 rounded-2xl p-6">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">이름</label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-slate-700 rounded-lg border border-slate-600 focus:border-primary-500 focus:outline-none transition-colors"
                    placeholder="홍길동"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">이메일</label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-slate-700 rounded-lg border border-slate-600 focus:border-primary-500 focus:outline-none transition-colors"
                    placeholder="email@example.com"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm text-slate-400 mb-2">문의 유형</label>
                <select
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-slate-700 rounded-lg border border-slate-600 focus:border-primary-500 focus:outline-none transition-colors"
                >
                  <option value="">선택하세요</option>
                  <option value="general">일반 문의</option>
                  <option value="bug">버그 신고</option>
                  <option value="suggestion">기능 제안</option>
                  <option value="report">사용자 신고</option>
                  <option value="business">제휴/비즈니스</option>
                  <option value="other">기타</option>
                </select>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm text-slate-400 mb-2">문의 내용</label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-slate-700 rounded-lg border border-slate-600 focus:border-primary-500 focus:outline-none transition-colors resize-none"
                  placeholder="문의 내용을 자세히 작성해주세요..."
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '전송 중...' : '문의 보내기'}
              </button>
            </form>
          )}
        </section>

        {/* Alternative Contact */}
        <section className="mb-12">
          <h3 className="text-lg font-semibold mb-4 text-center">다른 방법으로 연락하기</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <div className="text-2xl mb-2">📧</div>
              <p className="text-sm text-slate-400 mb-1">이메일</p>
              <a 
                href="mailto:repush9312@gmail.com" 
                className="text-primary-400 hover:text-primary-300 transition-colors"
              >
                repush9312@gmail.com
              </a>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <div className="text-2xl mb-2">💬</div>
              <p className="text-sm text-slate-400 mb-1">응답 시간</p>
              <p className="text-slate-300">평일 기준 24시간 이내</p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400 mb-4">
            <button onClick={() => navigate('/')} className="hover:text-white transition-colors">
              홈
            </button>
            <button onClick={() => navigate('/faq')} className="hover:text-white transition-colors">
              자주 묻는 질문
            </button>
            <button onClick={() => navigate('/guidelines')} className="hover:text-white transition-colors">
              커뮤니티 가이드라인
            </button>
          </div>
          <p className="text-center text-slate-500 text-xs">
            © 2024 말싸움. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
